import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Card, 
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  CheckCircle,
  AccessTime,
  MonetizationOn,
  RocketLaunch,
  CalendarMonth,
  Science as ScienceIcon,
  Engineering,
  FlashOn,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  GitHub as GitHubIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import { useProjects } from '../../hooks/useProjects';
import EditableImage from '../../components/ui/EditableImage';
import SEO from '../../components/seo/SEO';

// Fallback images
import aiImg from "../../assets/activities/conference.png";
import devImg from "../../assets/activities/training.png";
import labImg from "../../assets/activities/meetup.png";

const SalesTunnel = () => {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data: projectsData = [], isLoading: projectsLoading } = useProjects({ 
    language: i18n.language,
    isPublished: true,
    limit: 20
  });

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % projectsData.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + projectsData.length) % projectsData.length);
  };

  const getVisibleProjects = () => {
    if (projectsData.length === 0) return [];
    const items = [];
    const count = Math.min(3, projectsData.length);
    for (let i = 0; i < count; i++) {
      items.push(projectsData[(currentIndex + i) % projectsData.length]);
    }
    return items;
  };

  const getFallbackImage = (type) => {
    if (type === 'project') return devImg;
    if (type === 'lab') return labImg;
    return aiImg;
  };

  return (
    <Box sx={{ bgcolor: '#0B0F14', color: 'white', pt: 12, pb: 10 }}>
      <SEO 
        title={t('salesTunnel.seo.title', { defaultValue: 'AI Stack Consultant' })}
        description={t('salesTunnel.seo.description', { defaultValue: 'Consultant expert en IA et Data Science. Transformez vos données en valeur avec des solutions ML sur mesure.' })}
        keywords="consultant IA, data science, machine learning, freelance, expert"
      />
      {/* HERO SECTION */}
      <Container maxWidth="lg" sx={{ pt: 4, mb: 10 }}>
        <Grid container spacing={6} alignItems="center" sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Grid item xs={12} md={5}>
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <EditableImage 
                src="https://ejawolqfzczjrbtexuad.supabase.co/storage/v1/object/public/avatars/pitch_tunnel.PNG"
                alt="Alexandre SANOU"
                tableName="users"
                recordId="1"
                sx={{ 
                  width: '100%',
                  maxWidth: 450,
                  borderRadius: 4,
                  border: '2px solid rgba(212, 175, 55, 0.5)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.6), 0 0 20px rgba(212, 175, 55, 0.2)',
                  display: 'block',
                  mx: 'auto'
                }}
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} md={7}>
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#D4AF37', 
                  fontWeight: 800, 
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  mb: 1
                }}
              >
                Full-Stack Data Scientist & SaaS Builder
              </Typography>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 800, letterSpacing: 3, display: 'block', mb: 2 }}>
                {t("tunnel.badge")}
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 900, mb: 3, lineHeight: 1.1, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                {t("tunnel.title")}
              </Typography>
              <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: '600px', mx: { xs: 'auto', md: '0' } }}>
                {t("tunnel.subtitle")}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* BENEFITS GRID */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <Grid container spacing={3}>
          {[
            { icon: <AccessTime color="primary" />, text: t("tunnel.benefits.time") },
            { icon: <MonetizationOn color="primary" />, text: t("tunnel.benefits.money") },
            { icon: <RocketLaunch color="primary" />, text: t("tunnel.benefits.speed") }
          ].map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 3, border: '1px solid rgba(255,255,255,0.1)', height: '100%' }}>
                <Box sx={{ mb: 2 }}>{item.icon}</Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{item.text}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* LEAD MAGNET SECTION */}
      <Box sx={{ py: 12, bgcolor: 'rgba(212, 175, 55, 0.05)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <EditableImage 
                src="/assets/generated_images/ai-powered_sales_automation_dashboard.png" 
                alt={t("tunnel.leadMagnetTitle")}
                tableName="settings"
                recordId="sales_tunnel_hero"
                sx={{ 
                  width: '100%', 
                  borderRadius: 6, 
                  boxShadow: '0 30px 60px rgba(0,0,0,0.5)', 
                  border: '1px solid rgba(212, 175, 55, 0.3)' 
                }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>{t("tunnel.leadMagnetTitle")}</Typography>
              <Typography variant="h6" sx={{ color: '#D4AF37', mb: 4 }}>{t("tunnel.leadMagnetSubtitle")}</Typography>
              
              <Box sx={{ mb: 5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>{t("tunnel.preview.title")}</Typography>
                <List dense>
                  {[1,2,3,4,5].map(i => (
                    <ListItem key={i} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}><CheckCircle sx={{ color: '#4CAF50', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary={t(`tunnel.preview.auto${i}`)} />
                    </ListItem>
                  ))}
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><CheckCircle sx={{ color: '#D4AF37', fontSize: 20 }} /></ListItemIcon>
                    <ListItemText primary={t("tunnel.preview.bonus")} sx={{ fontWeight: 800 }} />
                  </ListItem>
                </List>
              </Box>

              <Paper sx={{ p: 4, bgcolor: 'white', borderRadius: 4, color: '#111' }}>
                <Stack spacing={2.5}>
                  <Button 
                    fullWidth 
                    variant="contained" 
                    size="large" 
                    component="a"
                    href="https://calendly.com/sanoualexandreism/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<CalendarMonth />}
                    sx={{ py: 2, bgcolor: '#D4AF37', color: '#111', fontWeight: 900, fontSize: '1.1rem', '&:hover': { bgcolor: '#B8962E' } }}>
                    {t("tunnel.form.button")}
                  </Button>
                  <Typography variant="caption" textAlign="center" sx={{ color: '#666' }}>{t("tunnel.form.security")}</Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* PROJECTS CAROUSEL SECTION */}
      <Box sx={{ py: 15, bgcolor: '#0B0F14' }}>
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 8 }}>
            <Box>
              <Typography 
                variant="overline" 
                sx={{ color: '#0FA3A3', fontWeight: 700, letterSpacing: 2 }}
              >
                Success Stories
              </Typography>
              <Typography 
                variant="h2" 
                sx={{ color: 'white', mt: 1, fontFamily: 'Philosopher' }}
              >
                Projets <span style={{ color: '#D4AF37' }}>Phare</span>
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton 
                onClick={handlePrev} 
                sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.1)', '&:hover': { borderColor: '#D4AF37' } }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <IconButton 
                onClick={handleNext} 
                sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.1)', '&:hover': { borderColor: '#D4AF37' } }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Stack>
          </Stack>

          <Box sx={{ position: 'relative' }}>
            {projectsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#0FA3A3' }} />
              </Box>
            ) : (
              <Grid container spacing={4}>
                <AnimatePresence mode="popLayout">
                  {getVisibleProjects().map((item, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={`${item.id}-${currentIndex}-${idx}`}>
                      <Box 
                        component={motion.div}
                        layout
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.5 }}
                        whileHover={{ y: -10 }}
                        sx={{ 
                          bgcolor: '#1F2937',
                          borderRadius: '24px',
                          overflow: 'hidden',
                          border: '1px solid rgba(255,255,255,0.05)',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: item.type === 'project' ? '#0FA3A3' : '#D4AF37',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
                          }
                        }}
                      >
                        <Box sx={{ height: '200px', position: 'relative', overflow: 'hidden' }}>
                          <EditableImage 
                            src={item.cover_image_url || getFallbackImage(item.type)}
                            alt={item.title}
                            tableName="projects"
                            recordId={item.id}
                            sx={{ width: '100%', height: '100%' }}
                            imgSx={{ filter: 'brightness(0.7)' }}
                          />
                          <Chip 
                            label={item.type.toUpperCase()} 
                            sx={{ 
                              position: 'absolute', 
                              top: 16, 
                              right: 16,
                              bgcolor: item.type === 'project' ? '#0FA3A3' : '#D4AF37',
                              color: item.type === 'project' ? 'white' : '#0B0F14',
                              fontWeight: 800,
                              fontSize: '0.7rem',
                              zIndex: 1
                            }} 
                          />
                        </Box>
                        
                        <Box sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, mb: 2, fontFamily: 'Philosopher' }}>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3, lineHeight: 1.6, flexGrow: 1 }}>
                            {item.description}
                          </Typography>
                          
                          {/* Technologies Section - Below description */}
                          {item.technologies?.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                              {item.technologies.slice(0, 4).map(techWrapper => (
                                <Chip 
                                  key={techWrapper.technology.id} 
                                  label={techWrapper.technology.name} 
                                  size="small"
                                  sx={{ 
                                    bgcolor: 'rgba(15, 163, 163, 0.05)', 
                                    color: '#0FA3A3', 
                                    border: '1px solid rgba(15, 163, 163, 0.2)',
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: '20px'
                                  }} 
                                />
                              ))}
                              {item.technologies.length > 4 && (
                                <Typography variant="caption" sx={{ color: '#9CA3AF', alignSelf: 'center' }}>
                                  +{item.technologies.length - 4}
                                </Typography>
                              )}
                            </Box>
                          )}
                          <Stack direction="row" spacing={2} sx={{ mt: 'auto' }}>
                            {item.github_url && (
                              <Button 
                                variant="outlined" 
                                startIcon={<GitHubIcon />}
                                href={item.github_url}
                                target="_blank"
                                size="small"
                                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.1)' }}
                              >
                                Code
                              </Button>
                            )}
                            {item.live_url && (
                              <Button 
                                variant="contained" 
                                startIcon={<LaunchIcon />}
                                href={item.live_url}
                                target="_blank"
                                size="small"
                                sx={{ bgcolor: item.type === 'project' ? '#0FA3A3' : '#D4AF37' }}
                              >
                                Demo
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            )}
          </Box>
        </Container>
      </Box>

      {/* STATS / SOCIAL PROOF */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={4} justifyContent="center">
          {[
            { n: "15+", l: t("tunnel.stats.downloadsLabel") },
            { n: "95%", l: t("tunnel.stats.deployedLabel") },
            { n: "< 15j", l: t("tunnel.stats.avgTimeLabel") }
          ].map((s, i) => (
            <Grid item xs={12} sm={4} key={i} textAlign="center">
              <Typography variant="h2" sx={{ fontWeight: 900, color: '#D4AF37' }}>{s.n}</Typography>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.6)' }}>{s.l}</Typography>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* TRUST SECTION */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Divider sx={{ mb: 10, borderColor: 'rgba(255,255,255,0.1)' }} />
        <Typography variant="h3" textAlign="center" sx={{ mb: 8, fontWeight: 800 }}>{t("tunnel.trust.title")}</Typography>
        <Grid container spacing={4} sx={{ mb: 12 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <ScienceIcon sx={{ fontSize: 40, color: '#D4AF37', mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 700 }}>{t("tunnel.trust.scientific")}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <Engineering sx={{ fontSize: 40, color: '#D4AF37', mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 700 }}>{t("tunnel.trust.pragmatic")}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <FlashOn sx={{ fontSize: 40, color: '#D4AF37', mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 700 }}>{t("tunnel.trust.speed")}</Typography>
            </Card>
          </Grid>
        </Grid>

        {/* TESTIMONIALS */}
        <Typography variant="h4" textAlign="center" sx={{ mb: 6, fontWeight: 800 }}>{t("tunnel.testimonials.title")}</Typography>
        <Grid container spacing={4}>
          {['valerie', 'jeanMichel', 'cedric', 'ahmed', 'chris'].map((key) => (
            <Grid item xs={12} md={4} key={key}>
              <Paper sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, height: '100%', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
                  "{t(`tunnel.testimonials.${key}.content`)}"
                </Typography>
                <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#D4AF37' }}>
                  {t(`tunnel.testimonials.${key}.name`)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {t(`tunnel.testimonials.${key}.role`)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CLOSING / CTA BOTTOM */}
      <Box sx={{ py: 12, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Paper sx={{ p: { xs: 4, md: 8 }, borderRadius: 6, bgcolor: '#1A1F26', border: '2px solid #D4AF37' }}>
            <Typography variant="h3" sx={{ fontWeight: 900, mb: 3 }}>{t("tunnel.closing.title")}</Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 6 }}>{t("tunnel.closing.subtitle")}</Typography>
            
            <Grid container spacing={2} sx={{ mb: 6, textAlign: 'left', maxWidth: '500px', mx: 'auto' }}>
              {[1,2,3].map(i => (
                <Grid item xs={12} key={i}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CheckCircle sx={{ color: '#D4AF37' }} />
                    <Typography variant="body1">{t(`tunnel.closing.item${i}`)}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            <Button variant="contained" size="large" endIcon={<CalendarMonth />}
              component="a"
              href="https://calendly.com/sanoualexandreism/30min"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ px: 6, py: 2.5, bgcolor: '#D4AF37', color: '#111', fontWeight: 900, borderRadius: 50, fontSize: '1.2rem', '&:hover': { bgcolor: '#B8962E' } }}>
              {t("tunnel.closing.button")}
            </Button>
            <Typography variant="body2" sx={{ mt: 3, color: '#4CAF50', fontWeight: 700 }}>{t("tunnel.closing.guarantee")}</Typography>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default SalesTunnel;
